import { Injector, inject } from '@angular/core';
import { BlockedUser } from '../models/blocked-user.model';
import { Actions, ofType } from '@ngrx/effects';
import { takeUntil } from 'rxjs';
import { userBlockChanged } from '../store/block-content';
import { MapSearchResult } from '../models/map-listing.model';
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';
import { AdCategories } from '../models/ad-categories.model';
import { AdReport, ReportSearch } from '../models/ad-report.model';
import { Car, CarSearchResult } from '../models/car-model';
import { CarSearch } from '../models/car-search.model';
import { CreateReport } from '../models/create-report.model';
import { RealEstateSearch } from '../models/real-estate-search.model';
import { RealEstate, RealEstateSearchResult } from '../models/real-estate.model';
import { User, UserSearchResult } from '../models/user.model';

@Injectable({
  providedIn: 'root',
})
export class ApiService {
  constructor(private http: HttpClient) {}
  private readonly injector = inject(Injector);
  private get contentChanged() { return this.injector.get(Actions).pipe(ofType(userBlockChanged)); }
  blockUser(id: number): Observable<void> { return this.http.put<void>(this.apiUrl + '/user/blocks/' + id, null); }
  unblockUser(id: number): Observable<void> { return this.http.delete<void>(this.apiUrl + '/user/blocks/' + id); }
  getBlockedUsers(): Observable<BlockedUser[]> { return this.http.get<BlockedUser[]>(this.apiUrl + '/user/blocks'); }
  reportUser(id: number, model: CreateReport): Observable<unknown> { return this.http.post(this.apiUrl + '/user/' + id + '/reports', model); }


  private apiUrl = environment.apiUrl + '/api';

  searchCars(searchParams: CarSearch): Observable<CarSearchResult> {
    return this.http.post<CarSearchResult>(this.apiUrl + '/cars/search', searchParams).pipe(takeUntil(this.contentChanged));
  }

  searchMapCars(searchParams: CarSearch): Observable<MapSearchResult> {
    return this.http.post<MapSearchResult>(this.apiUrl + '/cars/search/map', searchParams).pipe(takeUntil(this.contentChanged));
  }

  searchMapProperties(searchParams: RealEstateSearch): Observable<MapSearchResult> {
    return this.http.post<MapSearchResult>(this.apiUrl + '/RealEstate/search/map', searchParams).pipe(takeUntil(this.contentChanged));
  }

  getMapCards(category: 'car' | 'home', ids: number[]): Observable<(Car | RealEstate)[]> {
    return this.http.post<(Car | RealEstate)[]>(this.apiUrl + (category === 'car' ? '/cars' : '/RealEstate') + '/map/cards', { ids }).pipe(takeUntil(this.contentChanged));
  }

  getTopCars(): Observable<CarSearchResult> {
    return this.http.get<CarSearchResult>(this.apiUrl + '/cars/top').pipe(takeUntil(this.contentChanged));
  }

  getCarById(id: number): Observable<Car> {
    return this.http.get<Car>(`${this.apiUrl}/cars/${id}`).pipe(takeUntil(this.contentChanged));
  }

  addNewCar(carParams: FormData): Observable<Car> {
    return this.http.post<Car>(this.apiUrl + '/cars/new', carParams);
  }

  editCar(carParams: FormData, carId: number): Observable<Car> {
    return this.http.put<Car>(`${this.apiUrl}/cars/${carId}`, carParams);
  }

  upgradeCar(carId: number): Observable<Car> {
    return this.http.get<Car>(`${this.apiUrl}/cars/top/${carId}`);
  }

  setCarSold(carId: number): Observable<Car> {
    return this.http.get<Car>(`${this.apiUrl}/cars/sold/${carId}`);
  }

  deleteCar(carId: number): Observable<number> {
    return this.http.delete<number>(`${this.apiUrl}/cars/${carId}`);
  }

  likeCar(userId: number, carId: number, isLike: boolean): Observable<Car> {
    return this.http.put<Car>(`${this.apiUrl}/carLikes/${userId}/${carId}`, isLike);
  }

  getUserLikes(userId: number) {
    return this.http.get<Car[]>(`${this.apiUrl}/carLikes/user/${userId}`).pipe(takeUntil(this.contentChanged));
  }

  getCarLikesCount(carId: number) {
    return this.http.get<number>(`${this.apiUrl}/carLikes/car/${carId}`).pipe(takeUntil(this.contentChanged));
  }

  // Real Estates
  searchProperties(searchParams: RealEstateSearch): Observable<RealEstateSearchResult> {
    return this.http.post<RealEstateSearchResult>(this.apiUrl + '/RealEstate/search', searchParams).pipe(takeUntil(this.contentChanged));
  }

  getTopRealEstates(): Observable<RealEstateSearchResult> {
    return this.http.get<RealEstateSearchResult>(this.apiUrl + '/RealEstate/top').pipe(takeUntil(this.contentChanged));
  }

  getRealEstateById(id: number): Observable<RealEstate> {
    return this.http.get<RealEstate>(`${this.apiUrl}/realEstate/${id}`).pipe(takeUntil(this.contentChanged));
  }

  addNewRealEstate(realEstateParams: FormData): Observable<RealEstate> {
    return this.http.post<RealEstate>(this.apiUrl + '/realEstate/new', realEstateParams);
  }

  editRealEstate(realEstateParams: FormData, realEstateId: number): Observable<RealEstate> {
    return this.http.put<RealEstate>(`${this.apiUrl}/realEstate/${realEstateId}`, realEstateParams);
  }

  upgradeRealEstate(realEstateId: number): Observable<RealEstate> {
    return this.http.get<RealEstate>(`${this.apiUrl}/realEstate/top/${realEstateId}`);
  }

  setRealEstateSold(realEstateId: number): Observable<RealEstate> {
    return this.http.get<RealEstate>(`${this.apiUrl}/realEstate/sold/${realEstateId}`);
  }

  deleteRealEstate(realEstateId: number): Observable<number> {
    return this.http.delete<number>(`${this.apiUrl}/realEstate/${realEstateId}`);
  }

  likeRealEstate(userId: number, realEstateId: number, isLike: boolean): Observable<RealEstate> {
    return this.http.put<RealEstate>(
      `${this.apiUrl}/RealEstateLikes/${userId}/${realEstateId}`,
      isLike
    );
  }

  // Users
  searchUsers(searchParams: User): Observable<UserSearchResult[]> {
    return this.http.post<UserSearchResult[]>(this.apiUrl + '/user/search', searchParams).pipe(takeUntil(this.contentChanged));
  }

  getUserById(id: number): Observable<User> {
    return this.http.get<User>(`${this.apiUrl}/user/${id}`).pipe(takeUntil(this.contentChanged));
  }

  editUser(user: User, selectedFile?: File): Observable<User> {
    const formData = new FormData();
    formData.append('id', user.id.toString() || '');
    formData.append('name', user.name || '');
    formData.append('email', user.email || '');
    formData.append('status', user.status || '');
    formData.append('plan', user.plan || '');
    formData.append('phoneNumber', String(user.phoneNumber) || '');
    formData.append('officePhoneNumber', String(user.officePhoneNumber) || '');
    formData.append('officePhotoUrl', user.officePhotoUrl || '');
    formData.append('description', user.description || '');
    formData.append('planUpgradeMonths', user.planUpgradeMonths?.toString() || '');
    formData.append('city', user.city || '');
    formData.append('addressText', user.addressText || '');
    formData.append('latitude', user.latitude?.toString());
    formData.append('longitude', user.longitude?.toString());

    if (selectedFile) {
      formData.append('officePhoto', selectedFile);
    }

    return this.http.put<User>(`${this.apiUrl}/user/${user.id}`, formData);
  }

  updateUserLanguage(lang: string): Observable<User> {
    return this.http.put<User>(`${this.apiUrl}/user/language`, {
      language: lang,
    });
  }

  deleteUser(userId: number): Observable<User> {
    return this.http.delete<User>(`${this.apiUrl}/user/${userId}`);
  }

  deleteMyAccount(): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/user/me`);
  }

  getCategories(): Observable<AdCategories> {
    return this.http.get<AdCategories>(this.apiUrl + '/categories').pipe(takeUntil(this.contentChanged));
  }

  createReport(entity: string, id: number, model: CreateReport): Observable<AdReport> {
    return this.http.post<AdReport>(`${this.apiUrl}/${entity}/report/${id}`, model);
  }

  searchCarReports(reportSearch: ReportSearch): Observable<AdReport[]> {
    return this.http.post<AdReport[]>(`${this.apiUrl}/cars/reports`, reportSearch).pipe(takeUntil(this.contentChanged));
  }

  searchRealEstateReports(reportSearch: ReportSearch): Observable<AdReport[]> {
    return this.http.post<AdReport[]>(`${this.apiUrl}/realEstate/reports`, reportSearch).pipe(takeUntil(this.contentChanged));
  }

  updateCarReport(reportId: number, status: string): Observable<AdReport> {
    return this.http.post<AdReport>(`${this.apiUrl}/cars/report/${reportId}/status`, { status });
  }

  updateRealEstateReport(reportId: number, status: string): Observable<AdReport> {
    return this.http.post<AdReport>(`${this.apiUrl}/realEstate/report/${reportId}/status`, {
      status,
    });
  }
}
